import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

function NewTaskDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", due_date: "", priority: "medium" });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/tasks", { ...f, due_date: f.due_date || null })).data,
    onSuccess: () => { toast.success("Task created"); qc.invalidateQueries({ queryKey: ["tasks"] }); setOpen(false); setF({ title: "", description: "", due_date: "", priority: "medium" }); },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="new-task-btn"><Plus size={14} /> New Task</button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }} className="space-y-3" data-testid="new-task-form">
          <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Due date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due" /></div>
            <div>
              <Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full" data-testid="task-submit">Create Task</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const PRIORITY_STYLES = { low: "bg-slate-100 text-slate-700", medium: "bg-amber-100 text-amber-800", high: "bg-red-100 text-red-800" };

export default function Tasks() {
  const qc = useQueryClient();
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: async () => (await api.get("/tasks")).data });
  const complete = useMutation({
    mutationFn: async (id) => (await api.patch(`/tasks/${id}`, { status: "done" })).data,
    onSuccess: () => { toast.success("Marked done"); qc.invalidateQueries({ queryKey: ["tasks"] }); },
  });
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Daily Tasks</h1>
          <p className="text-sm text-[#52525B]">Personal to-dos and team assignments.</p>
        </div>
        <NewTaskDialog />
      </div>
      <div className="mt-6 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="tasks-table">
          <thead><tr><th>Title</th><th>Priority</th><th>Due</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {tasks.length === 0 && <tr><td colSpan="5" className="text-center text-[#52525B] py-8">No tasks.</td></tr>}
            {tasks.map((t) => (
              <tr key={t.id}>
                <td className="font-medium">{t.title}<div className="text-xs text-[#52525B]">{t.description}</div></td>
                <td><Badge className={PRIORITY_STYLES[t.priority]} variant="secondary">{t.priority}</Badge></td>
                <td className="text-xs">{t.due_date || "—"}</td>
                <td><Badge variant="secondary">{t.status}</Badge></td>
                <td>{t.status !== "done" && <button onClick={() => complete.mutate(t.id)} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`task-done-${t.id}`}><CheckCircle size={12} /> Done</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
